"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { MapPinAreaIcon, StorefrontIcon } from "@phosphor-icons/react";
import StaticMapImage from "@/components/map/StaticMapImage";
import { latFromMercY, lngFromMercX, mercX, mercY } from "@/lib/map/mercator";
import type { BlogLangCode, EmbeddedPartnerBranch } from "@/lib/api/blog.server";

interface Props {
  locale: BlogLangCode;
  partnerName: string;
  branches: EmbeddedPartnerBranch[];
}

const MAX_STATIC_SIZE = 640;

const COPY: Record<BlogLangCode, { title: string; hint: string; address: string }> = {
  ko: { title: "운영 지점 지도", hint: "숫자를 누르면 해당 지역을 확대하고, 핀을 누르면 지점을 확인할 수 있어요.", address: "주소" },
  en: { title: "Service location map", hint: "Select a number to zoom into an area, or a pin for location details.", address: "Address" },
  ja: { title: "運営拠点マップ", hint: "数字を押すと地域を拡大し、ピンを押すと拠点情報を確認できます。", address: "住所" },
  "zh-CN": { title: "运营网点地图", hint: "点击数字放大区域，点击图钉查看网点。", address: "地址" },
  "zh-TW": { title: "營運據點地圖", hint: "點選數字放大區域，點選圖釘查看據點。", address: "地址" },
};

export function PartnerLocationsMapClient({ locale, partnerName, branches }: Props) {
  const copy = COPY[locale];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusIds, setFocusIds] = useState<string[] | null>(null);
  const [zoomBoost, setZoomBoost] = useState(0);
  const visibleBranches = useMemo(
    () => focusIds ? branches.filter(branch => focusIds.includes(branch.id)) : branches,
    [branches, focusIds],
  );
  const resetView = () => { setFocusIds(null); setZoomBoost(0); setSelectedId(null); };
  const selectedBranch = branches.find((branch) => branch.id === selectedId) ?? (focusIds ? visibleBranches[0] : null);
  const selectedIndex = selectedBranch ? visibleBranches.findIndex(branch => branch.id === selectedBranch.id) : -1;
  const mapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const element = mapRef.current;
    if (!element) return;
    const measure = () => {
      const width = element.clientWidth;
      const height = element.clientHeight;
      if (width > 0 && height > 0) setSize({ width, height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const geometry = useMemo(() => {
    if (!size || visibleBranches.length === 0) return null;
    const xs = visibleBranches.map((branch) => mercX(branch.longitude));
    const ys = visibleBranches.map((branch) => mercY(branch.latitude));
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const centerX = (minX + maxX) / 2;
    const boundsCenterY = (minY + maxY) / 2;
    // Keep markers above the caption and map attribution at the bottom.
    const paddingTop = 24;
    const paddingBottom = 72;
    const availableWidth = Math.max(1, size.width - 48);
    const availableHeight = Math.max(1, size.height - paddingTop - paddingBottom);
    const spanX = Math.max((maxX - minX) * 1.3, 1e-6);
    const spanY = Math.max((maxY - minY) * 1.3, 1e-6);
    const fitZoom = visibleBranches.length === 1
      ? 14
      : Math.max(4, Math.min(15, Math.floor(Math.min(
        Math.log2(availableWidth / (256 * spanX)),
        Math.log2(availableHeight / (256 * spanY)),
      ))));
    const zoom = Math.min(18, fitZoom + zoomBoost);
    const imageWidth = Math.min(Math.round(size.width), MAX_STATIC_SIZE);
    const imageHeight = Math.min(Math.round(size.height), MAX_STATIC_SIZE);
    const scale = Math.max(size.width / imageWidth, size.height / imageHeight);
    const world = 256 * 2 ** zoom;
    const centerY = boundsCenterY + (paddingBottom - paddingTop) / (2 * world * scale);
    return {
      center: { lat: latFromMercY(centerY), lng: lngFromMercX(centerX) },
      zoom,
      project: (latitude: number, longitude: number) => ({
        x: size.width / 2 + (mercX(longitude) - centerX) * world * scale,
        y: size.height / 2 + (mercY(latitude) - centerY) * world * scale,
      }),
    };
  }, [visibleBranches, size, zoomBoost]);

  const markers = useMemo(() => {
    if (!geometry) return [];
    const points = visibleBranches.map(branch => ({ branch, ...geometry.project(branch.latitude, branch.longitude) }));
    const groups: typeof points[] = [];
    // Limit each group's diameter instead of joining chains of nearby points.
    // A dense chain across Japan must not collapse into a single marker.
    for (const point of [...points].sort((a, b) => a.x - b.x || a.y - b.y)) {
      const group = groups.find(items => items.every(item =>
        Math.hypot(point.x - item.x, point.y - item.y) < 44,
      ));
      if (group) group.push(point);
      else groups.push([point]);
    }
    if (selectedId) {
      for (const group of groups) {
        const index = group.findIndex(point => point.branch.id === selectedId);
        if (index !== -1 && group.length > 1) {
          const [selected] = group.splice(index, 1);
          groups.push([selected]);
          break;
        }
      }
    }
    return groups.map(group => ({
      branches: group.map(point => point.branch),
      x: group.reduce((sum, point) => sum + point.x, 0) / group.length,
      y: group.reduce((sum, point) => sum + point.y, 0) / group.length,
    })).filter(point => size && point.x >= 0 && point.x <= size.width && point.y >= 0 && point.y <= size.height);
  }, [geometry, visibleBranches, size, selectedId]);

  if (branches.length === 0) return null;

  return (
    <div className="not-prose my-8 space-y-3">
    <section className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
      <div className="flex items-start justify-between gap-4 border-b border-zinc-100 px-5 py-4">
        <div>
          <h3 className="m-0 text-base font-semibold text-zinc-950">{copy.title}</h3>
          <p className="mt-1 text-sm leading-6 text-zinc-500">{copy.hint}</p>
        </div>
        <span className="shrink-0 rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700">{branches.length}</span>
      </div>
      <div ref={mapRef} className="relative h-80 overflow-hidden bg-zinc-100">
        {geometry && (
          <>
            <StaticMapImage
              center={geometry.center}
              zoom={geometry.zoom}
              showMarkers={false}
              title={`${partnerName} ${copy.title}`}
              alt={`${partnerName} ${copy.title}`}
            />
            <button
              type="button"
              className="absolute inset-0 z-0 cursor-default"
              aria-label={copy.hint}
              tabIndex={-1}
              onClick={() => setSelectedId(null)}
            />
            {markers.map((point) => {
              const branch = point.branches[0];
              const clustered = point.branches.length > 1;
              const isSelected = branch.id === selectedId;
              return (
                <button
                  key={branch.id}
                  type="button"
                  className={`absolute ${isSelected ? "z-20" : "z-10"} -translate-x-1/2 -translate-y-1/2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500`}
                  style={{ left: `${point.x}px`, top: `${point.y}px` }}
                  title={clustered ? point.branches.map(item => item.name).join(" · ") : branch.name}
                  aria-label={clustered ? point.branches.length + " · " + copy.title : branch.name}
                  onClick={() => {
                    if (clustered) {
                      setFocusIds(point.branches.map(item => item.id));
                      setZoomBoost(point.branches.length === visibleBranches.length ? zoomBoost + 2 : 0);
                      setSelectedId(null);
                    } else setSelectedId(branch.id);
                  }}
                >
                  <span className={`relative flex items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-orange-200 transition-transform hover:scale-110 ${clustered ? "h-7 w-7" : "h-[22px] w-[22px]"} ${isSelected ? "ring-2 ring-orange-500" : ""}`}>
                    <span className={`flex items-center justify-center rounded-full bg-orange-500 text-white ${clustered ? "h-[23px] w-[23px]" : "h-[18px] w-[18px]"}`}>
                      {clustered ? <span className="text-[10px] font-semibold leading-none">{point.branches.length}</span> : <StorefrontIcon className="h-2.5 w-2.5" weight="bold" />}
                    </span>
                  </span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </section>
    {(focusIds || selectedBranch) && (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          {focusIds ? (
            <button type="button" onClick={resetView} className="inline-flex min-h-9 items-center rounded-full border border-zinc-200 bg-white px-3.5 py-2 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500">
              ← {{ ko: "전체 지도", en: "All locations", ja: "全体マップ", "zh-CN": "全部网点", "zh-TW": "全部據點" }[locale]}
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" disabled={selectedIndex <= 0} onClick={() => setSelectedId(visibleBranches[selectedIndex - 1].id)} aria-label={{ko:"이전 지점",en:"Previous location",ja:"前の拠点","zh-CN":"上一网点","zh-TW":"上一據點"}[locale]} className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200 text-zinc-700 disabled:opacity-30">‹</button>
            <span className="min-w-12 text-center text-xs tabular-nums text-zinc-500">{selectedIndex + 1} / {visibleBranches.length}</span>
            <button type="button" disabled={selectedIndex >= visibleBranches.length - 1} onClick={() => setSelectedId(visibleBranches[selectedIndex + 1].id)} aria-label={{ko:"다음 지점",en:"Next location",ja:"次の拠点","zh-CN":"下一网点","zh-TW":"下一據點"}[locale]} className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200 text-zinc-700 disabled:opacity-30">›</button>
          </div>
        </div>
        {selectedBranch && (
          <div aria-live="polite" className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-4">
            <MapPinAreaIcon className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" />
            <div className="min-w-0 flex-1">
              <p className="m-0 text-sm font-semibold text-zinc-900">{selectedBranch.name}</p>
              <p className="mb-2 mt-1 text-xs leading-5 text-zinc-500">{selectedBranch.address}{selectedBranch.addressDetail ? `, ${selectedBranch.addressDetail}` : ""}</p>
              <a href={`https://www.google.com/maps/search/?api=1&query=${selectedBranch.latitude},${selectedBranch.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-orange-600 underline underline-offset-4">
                {{ko:"지도에서 보기",en:"Open in Maps",ja:"地図で見る","zh-CN":"在地图中查看","zh-TW":"在地圖中查看"}[locale]}
              </a>
            </div>
          </div>
        )}
      </div>
    )}
    </div>
  );
}
